import type {
  Flow___Argument,
  FlowData___ConditionRepresentation,
} from '~/graphql/types';
import type { InstanceMap } from '../../../Selector/utils/getInstance';
import type { DirectoryMap } from '../../../Selector/utils/getDirectory';
import { getSubject, SubjectMap } from '../../../Selector/utils/getSubject';
import getPathForPointer from '../../../Selector/utils/getPathForPointer';
import type { FlowPointerFields } from '../../../Selector/utils/getFieldsByPath';
import type { PrimitiveInputMap } from '../../../Selector/utils/getPrimitiveInput';
import type { ConditionMap } from '../../../Selector/utils/getConditions';
import getRenderedArgument, {
  RenderArgumentContext,
} from './getRenderedArgument';
import renderHandlebarsTemplate from '~/util/handlebars';
import { assertNever } from '~/util/assertNever';
import type { Flow___ConditionPartial } from '../../types';

export const getPathRepresentation = (path: Array<FlowPointerFields>): string =>
  path.reduce((prev, curr, index) => {
    if (index === 0) return curr.label;

    return `${prev} > ${curr.label}`;
  }, '');

export const text = {
  invalidVariable: 'Fout - Waarde bestaat niet!',
  emptyArg: '""',
};

export type ConditionLabelType =
  /** This is an input */
  | { type: 'input'; hasError?: boolean }
  /** This is text only */
  | { type: 'text'; hasError?: boolean }
  /** This is an argument - __typename gives a more concrete type if known */
  | {
      type: 'argument';
      __typename: Flow___Argument['__typename'] | null;
      hasError?: boolean;
    };

export type GetLabelForRepReturnType =
  | {
      error?: undefined;
      result: string;
      type: ConditionLabelType;
    }
  | { error: string; type: ConditionLabelType };

export type Args = {
  representation: FlowData___ConditionRepresentation;
  condition?: Flow___ConditionPartial | null;
  ctx: RenderArgumentContext;
};

export type Opts = {
  instanceMap: InstanceMap;
  subjectMap: SubjectMap;
  directoryMap: DirectoryMap;
  conditionMap: ConditionMap;
  primitiveInputMap: PrimitiveInputMap;
};

/**
 * Returns a label for a condition or an error if it cannot be resolved.
 */
const getLabelForRep = (
  { representation, condition, ctx }: Args,
  opts: Opts,
): GetLabelForRepReturnType => {
  const {
    instanceMap,
    subjectMap,
    directoryMap,
    conditionMap,
    primitiveInputMap,
  } = opts;
  const { variable, rep } = representation;
  const type = ((): ConditionLabelType => {
    if (variable == null) return { type: 'text' };
    if (variable === 0) return { type: 'input' };

    return { type: 'argument', __typename: null };
  })();

  if (condition == null) {
    return {
      result: renderHandlebarsTemplate(rep, { ctx }),
      type: { type: 'text' },
    };
  }
  if (variable == null) {
    return {
      result: renderHandlebarsTemplate(rep, { ctx }),
      type: { type: 'text' },
    };
  }

  switch (condition.__typename) {
    case 'Flow___InstanceCondition': {
      if (variable === 0) {
        const path = getPathForPointer(condition.input, {
          subjectMap,
          instanceMap,
          directoryMap,
        });

        if (path.error != null) {
          return {
            error: text.invalidVariable,
            type: { ...type, hasError: true },
          };
        }

        return {
          result: renderHandlebarsTemplate(rep, {
            pointer: getPathRepresentation(path.fullResult),
            ctx,
          }),
          type,
        };
      }

      const argumentIdx = variable - 1;
      const argument = condition.args[argumentIdx];
      if (argument == null) return { result: text.emptyArg, type };

      const conditionFlowData = conditionMap[condition.type];
      const argumentFlowData = conditionFlowData.args[argumentIdx];
      const primitiveInput = primitiveInputMap[argumentFlowData.type];

      const arg = getRenderedArgument(
        {
          argument,
          rep,
          variable,
          primitiveInput,
          condition,
          ctx,
        },
        opts,
      );

      return {
        ...arg,
        type: {
          type: 'argument',
          __typename: argument.__typename,
          hasError: arg.error != null,
        },
      };
    }
    case 'Flow___SubjectFieldCondition': {
      if (variable == 0) return { result: rep, type };

      const argumentIdx = variable - 1;
      const argument = condition.args[argumentIdx];
      if (argument == null) return { result: text.emptyArg, type };

      const subject = getSubject(
        {
          type: condition.type,
          typeId: condition.typeId,
        },
        subjectMap,
      );

      const subjectFieldCondition = subject?.fields.find(
        field => field.key === condition.field,
      );
      if (!subjectFieldCondition) {
        return {
          error: text.invalidVariable,
          type: { ...type, hasError: true },
        };
      }

      const argumentFlowData = subjectFieldCondition.args[argumentIdx];
      const primitiveInput = primitiveInputMap[argumentFlowData.type];

      const arg = getRenderedArgument(
        {
          argument,
          rep,
          variable,
          primitiveInput,
          condition,
          ctx,
        },
        opts,
      );

      return {
        ...arg,
        type: {
          type: 'argument',
          __typename: argument.__typename,
          hasError: arg.error != null,
        },
      };
    }
    default:
      return assertNever(condition);
  }
};

export default getLabelForRep;
